import React from "react";
import { CheckCircle, XCircle, ArrowRight } from "lucide-react";

const LessonQuizView = ({
  lessonTitle,
  lessonIndex,
  totalLessons,
  question,
  currentIndex,
  totalQuestions,
  selectedAnswer,
  isAnswered,
  onSelectAnswer,
  onNext,
  isAlreadyTaken = false,
  quizScore,
  onProceed,
  onReviewLessons,
}) => {
  if (isAlreadyTaken) {
    return (
      <div className="space-y-4 text-left">
        <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-emerald-600 text-white font-gilroyBold text-xs px-2.5 py-0.5 rounded-full">
                {lessonIndex !== undefined && totalLessons
                  ? `Lesson ${lessonIndex + 1} of ${totalLessons} Quiz`
                  : "Knowledge Check"}
              </span>
              {lessonTitle && (
                <span className="font-gilroyBold text-xs text-gray-600 truncate max-w-[200px] md:max-w-xs">
                  {lessonTitle}
                </span>
              )}
            </div>
            <span className="text-gray-500 text-[13px]">
              Quiz Already Completed
            </span>
          </div>
          <span className="bg-emerald-50 text-emerald-800 font-gilroyBold text-xs px-2.5 py-1 rounded-full border border-emerald-200/80">
            Cannot Retake
          </span>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-5 md:p-6 shadow-sm text-center space-y-4">
          <div className="w-12 h-12 rounded-xl bg-gray-900 text-white flex items-center justify-center mx-auto shadow-xs">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-gilroyBold text-lg text-gray-900 m-0">
              Quiz Already Completed
            </h3>
            <p className="text-gray-600 text-[13px] mt-1.5 max-w-md mx-auto leading-relaxed">
              You have already taken and submitted this ambassador quiz. As indicated, qualifying quizzes cannot be retaken once submitted.
            </p>
          </div>

          {quizScore !== undefined && quizScore !== null && (
            <div className="inline-block bg-emerald-50 border border-emerald-200/80 rounded-xl px-4 py-2">
              <span className="text-[13px] text-emerald-900 font-gilroyBold">
                Your Recorded Score: {quizScore} Pts
              </span>
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
            {onReviewLessons && (
              <button
                type="button"
                onClick={onReviewLessons}
                className="w-full sm:flex-1 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-gilroyBold text-[13px] rounded-xl border border-gray-200/80 shadow-xs transition cursor-pointer"
              >
                Revision (Back)
              </button>
            )}
            {onProceed && (
              <button
                type="button"
                onClick={onProceed}
                className="w-full sm:flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-gilroyBold text-[13px] rounded-xl shadow-xs border-0 cursor-pointer transition flex items-center justify-center gap-2"
              >
                <span>
                  {lessonIndex !== undefined && totalLessons && lessonIndex < totalLessons - 1
                    ? `Proceed to Lesson ${lessonIndex + 2}`
                    : "View Assessment Result"}
                </span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!question) return null;

  const isCorrect = selectedAnswer === question.correctAnswer;
  const isLastQuestion = currentIndex === totalQuestions - 1;

  return (
    <div className="space-y-4 text-left">
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-emerald-600 text-white font-gilroyBold text-xs px-2.5 py-0.5 rounded-full">
              {lessonIndex !== undefined && totalLessons
                ? `Lesson ${lessonIndex + 1} of ${totalLessons} Quiz`
                : "Knowledge Check"}
            </span>
            {lessonTitle && (
              <span className="font-gilroyBold text-xs text-gray-600 truncate max-w-[200px] md:max-w-xs">
                {lessonTitle}
              </span>
            )}
          </div>
          <span className="text-gray-500 text-[13px]">
            Question {currentIndex + 1} of {totalQuestions}
          </span>
        </div>
        <span className="bg-blue-50 text-blue-800 font-gilroyBold text-xs px-2.5 py-1 rounded-full border border-blue-200/80">
          Single Attempt
        </span>
      </div>

      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-4">
        <div className="border-b border-gray-100 pb-3 flex justify-between items-start gap-3">
          <div>
            <span className="text-emerald-700 font-gilroyBold text-xs uppercase tracking-wide">
              Question {currentIndex + 1}
            </span>
            <h3 className="font-gilroyBold text-base md:text-lg text-gray-900 mt-1 leading-snug">
              {question.question}
            </h3>
          </div>
          <span className="bg-emerald-50 text-emerald-800 font-gilroyBold text-xs px-2.5 py-1 rounded-full border border-emerald-200/80 whitespace-nowrap flex-shrink-0">
            {question.points !== undefined && question.points !== null ? question.points : 2} {Number(question.points) === 1 ? "Pt" : "Pts"}
          </span>
        </div>

        <div className="space-y-2.5">
          {question.options.map((option, idx) => {
            const isSelected = selectedAnswer === idx;
            const isCorrectAnswer = question.correctAnswer === idx;

            let buttonStyle = "border-gray-200 bg-white text-gray-800 hover:border-gray-300";
            let badgeIcon = (
              <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-gilroyBold flex items-center justify-center flex-shrink-0 text-xs">
                {String.fromCharCode(65 + idx)}
              </span>
            );

            if (isAnswered) {
              if (isSelected && isCorrect) {
                buttonStyle =
                  "border-emerald-500 bg-emerald-50 text-emerald-900 font-gilroyBold ring-2 ring-emerald-500";
                badgeIcon = (
                  <span className="w-7 h-7 rounded-lg bg-emerald-600 text-white font-gilroyBold flex items-center justify-center flex-shrink-0 text-xs">
                    ✓
                  </span>
                );
              } else if (isSelected && !isCorrect) {
                buttonStyle =
                  "border-red-500 bg-red-50 text-red-900 font-gilroyBold ring-2 ring-red-500";
                badgeIcon = (
                  <span className="w-7 h-7 rounded-lg bg-red-600 text-white font-gilroyBold flex items-center justify-center flex-shrink-0 text-xs">
                    ✕
                  </span>
                );
              } else if (isCorrectAnswer) {
                buttonStyle =
                  "border-emerald-400 bg-emerald-50/50 text-emerald-800 font-gilroyBold";
                badgeIcon = (
                  <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-800 font-gilroyBold flex items-center justify-center flex-shrink-0 text-xs">
                    ✓
                  </span>
                );
              } else {
                buttonStyle = "border-gray-200 bg-gray-50 text-gray-400 opacity-60";
              }
            }

            return (
              <button
                key={idx}
                type="button"
                disabled={isAnswered}
                onClick={() => onSelectAnswer(question.id, idx)}
                className={`w-full p-3.5 rounded-xl border text-left text-[13px] font-medium transition-all flex items-center gap-3 cursor-pointer ${buttonStyle}`}
              >
                {badgeIcon}
                <span className="flex-1 leading-snug">{option}</span>
              </button>
            );
          })}
        </div>

        {isAnswered && (
          <div
            className={`rounded-xl p-3.5 border space-y-1.5 animate-fadeIn ${
              isCorrect
                ? "bg-emerald-50/80 border-emerald-200 text-emerald-900"
                : "bg-red-50/80 border-red-200 text-red-900"
            }`}
          >
            <div className="flex items-center gap-2">
              {isCorrect ? (
                <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              )}
              <span className="font-gilroyBold text-[13px]">
                {isCorrect ? "Correct Mark!" : "Incorrect Mark"}
              </span>
            </div>
            <p className="text-[13px] text-gray-700 m-0 leading-relaxed pt-0.5">
              <strong className="font-gilroyBold text-gray-900">Explanation: </strong>
              {question.explanation}
            </p>
          </div>
        )}
      </div>

      {isAnswered && (
        <button
          type="button"
          onClick={onNext}
          className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-gilroyBold text-[13px] rounded-xl border-0 shadow-xs cursor-pointer transition flex items-center justify-center gap-2"
        >
          <span>
            {isLastQuestion
              ? lessonIndex !== undefined && totalLessons && lessonIndex === totalLessons - 1
                ? "Complete Training & View Final Score"
                : "Complete Lesson Quiz & Next Lesson"
              : "Next Question"}
          </span>
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default LessonQuizView;
